import type { IconName, Omittable } from "@salestrak/ui";

import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

// The settings sidebar's destinations — the one registry of settings pages
// and their permission gates. The sidebar renders it, the settings search
// indexes it, and the global search derives its "Go to … Settings" commands
// from it, so a page added here reaches every door at once.

type NavItemData = Omittable<{
  icon: IconName;
  selectedIcon?: IconName;
  label: string;
  route: string;
  badge?: number;
  exact?: boolean;
  /** Opts the page into the global search's command list under this "Go to"
   *  label. Only the pages people reach for often carry one — the personal
   *  pages and the two admin pages that come up daily. Every other page is a
   *  step past "Go to Settings" (and the settings search), so listing them
   *  would bloat the command list without helping anyone find them. */
  commandLabel?: string;
}>;

export const USER_SETTINGS: GatedNavItemData[] = [
  {
    icon: "sliders-horizontal",
    selectedIcon: "sliders-horizontal-filled",
    label: "Preferences",
    route: "/settings/preferences",
    commandLabel: "Go to Preferences",
  },
  {
    icon: "circle-user",
    selectedIcon: "circle-user-filled",
    label: "Profile",
    route: "/settings/profile",
    commandLabel: "Go to Profile Settings",
  },
  // The same glyph the account menu's Theme sub-menu wears — the two doors
  // into the theme system read as one feature.
  {
    icon: "sun",
    selectedIcon: "sun-filled",
    label: "Appearance",
    route: "/settings/appearance",
    commandLabel: "Go to Appearance Settings",
  },
  // Open to every member: the page carries account security (password),
  // and gates its API-keys section on `workspace.integrations` itself.
  {
    icon: "user-lock",
    selectedIcon: "user-lock-filled",
    label: "Security & access",
    route: "/settings/access",
  },
];

// Each gated item maps to the permission key that controls editing in
// that settings area. Two visibility grammars:
//
//   * Administrative pages (roles, members, workflows…): the viewer who
//     can't edit sees nothing — the row is hidden entirely (Permissions
//     2.0 page gating).
//   * Vocabulary pages (`readOnlyView`): the values the page defines show
//     on every entity the viewer touches, so the page itself is reference
//     material — the row shows for every internal viewer and the PAGE drops
//     its edit affordances without the keys. Guests (every held role at the
//     Guest rung) stay outside workspace configuration entirely.
//
// `discoveryKey` names a discovery flag (see useDiscovery) — the row wears
// a "New" badge until the viewer opens it, and pressing the row dismisses
// the flag.
export type GatedNavItemData = NavItemData & Omittable<{
  permKey?: string;
  /** Any ONE of these permissions reveals the destination. For a page that
   *  several governance keys reach and that shows each viewer only the part
   *  their keys cover — `permKey` cannot express that. */
  permKeys?: string[];
  /** The page renders read-only without the perm keys, so the row shows for
   *  every internal (non-guest) viewer. The route still gates guests. */
  readOnlyView?: boolean;
  discoveryKey?: string;
}>;

export const FEATURE_ITEMS: GatedNavItemData[] = [
  {
    icon: "shapes",
    selectedIcon: "shapes-filled",
    label: "Types",
    permKey: "issue_types.manage",
    readOnlyView: true,
    route: "/settings/issue-types",
  },
  // One shared pool across objects, so it sits with the cross-object features
  // rather than under a Projects heading.
  {
    icon: PROPERTY_ICON.issueSource,
    label: "Sources",
    permKey: "issue_sources.manage",
    readOnlyView: true,
    route: "/settings/issue-sources",
  },
  // The consolidated Outcomes page — posting rules and vocabularies as tabs.
  // The three governance keys gate the edit affordances tab by tab; the page
  // reads the same for everyone.
  {
    icon: PROPERTY_ICON.followUp,
    selectedIcon: "heart-pulse-filled",
    label: "Outcomes",
    permKeys: ["issue_types.manage", "manage.edit_workflows", "reasons.manage"],
    readOnlyView: true,
    route: "/settings/outcomes",
  },
  {
    discoveryKey: "forms",
    icon: "clipboard-check",
    selectedIcon: "clipboard-check-filled",
    label: "Forms",
    permKey: "forms.manage",
    route: "/settings/forms",
  },
  // Self-serve (the ClickUp member model): trash.manage holds the workspace
  // trash and its destructive verbs, but anyone who can delete an entity can
  // open the page to restore their own deletions.
  {
    icon: "trash",
    selectedIcon: "trash-filled",
    label: "Trash",
    permKeys: ["trash.manage", "delete.items"],
    route: "/settings/trash",
  },
];

export const ADMINISTRATION_ITEMS: GatedNavItemData[] = [
  // create.roles is the backend's own gate on updateWorkspace, so the row
  // shows exactly when the page's edits would be allowed.
  {
    icon: "orbit",
    selectedIcon: "orbit-filled",
    label: "Workspace",
    permKey: "create.roles",
    route: "/settings/workspace",
    commandLabel: "Go to Workspace Settings",
  },
  {
    icon: "user",
    selectedIcon: "user-filled",
    label: "Members",
    permKey: "manage.users",
    route: "/settings/members",
    commandLabel: "Go to Members Settings",
  },
  {
    icon: "users",
    selectedIcon: "users-filled",
    label: "Teams",
    permKey: "manage.teams",
    route: "/settings/teams",
  },
  {
    discoveryKey: "permissions",
    icon: "shield",
    selectedIcon: "shield-filled",
    label: "Permissions",
    permKey: "create.roles",
    route: "/settings/permissions",
  },
  {
    exact: true,
    icon: ENTITY_KIND_ICON.workflow,
    selectedIcon: "workflow-filled",
    label: "Workflows",
    permKey: "manage.edit_workflows",
    route: "/settings/workflows",
  },
  {
    icon: "webhook",
    label: "API",
    permKey: "workspace.integrations",
    route: "/settings/api",
  },
];

// The three route-less manager dialogs. Their gating is not a flat perm key
// (flags + provider catalog take part), so it lives in useManageDialogDoors
// and none of them carries a `permKey`.
export const CUSTOM_FIELDS_ITEM: GatedNavItemData = {
  icon: "swatch-book",
  selectedIcon: "swatch-book-filled",
  label: "Custom Field Manager",
  route: "",
};

export const AUTOMATIONS_ITEM: GatedNavItemData = {
  icon: "zap",
  selectedIcon: "zap-filled",
  label: "Automations Manager",
  route: "",
};

export const INTEGRATIONS_ITEM: GatedNavItemData = {
  icon: "puzzle",
  label: "App Center",
  route: "",
};


/**
 * Whether a gated item shows for a viewer, given a permission predicate.
 * `permKey` and `permKeys` compose: the single key must pass AND at least one
 * of the alternatives must. An ungated item always shows. A `readOnlyView`
 * item also shows to a viewer the caller marks eligible for read-only pages
 * (an internal, non-guest viewer) — the page drops its edit affordances.
 */
export function isNavItemVisible(
  item: GatedNavItemData,
  has: (permKey: string) => boolean,
  opts?: { readOnlyViews?: boolean },
): boolean {
  if (item.readOnlyView && opts?.readOnlyViews) return true;
  return (
    (!item.permKey || has(item.permKey)) &&
    (!item.permKeys || item.permKeys.some((key) => has(key)))
  );
}
